import ContentBlocks from 'components/ContentBlocks'
import Hero from 'components/Hero'
import Layout from 'components/Layout'
import Seo from 'components/Seo'
import { graphql } from 'gatsby'
import { useStores } from 'hooks/useStores'
import { observer } from 'mobx-react'
import PropTypes from 'prop-types'
import React, { useEffect } from 'react'
import style from 'templates/Page.module.scss'

const Page = ({ data }) => {
  const { uiStore } = useStores()

  const { page, banner, navigation, allResources } = data

  useEffect(() => {
    uiStore.setCurrentPage(page)
  }, [uiStore, page])

  const meta = [ 
    {
      property: 'robots',
      content: 'index',
    },
  ]

  return (
    <Layout navigation={navigation} banner={banner} allResources={allResources}>
      <div className={style.root}>
        <Seo
          title={page.metaTitle || page.title}
          description={page.metaDescription?.metaDescription}
          image={page.metaImage?.fixed?.src}
          meta={meta}
          slug={page.slug === 'home' ? `/` : `/${page.slug}`}
        />
        <div className={style.hero}>
          <Hero
            heading={page.heroHeading}
            introduction={page.heroIntroduction?.heroIntroduction}
            image={page.heroImage}
            statements={page.heroStatements}
            statementsPosition={page.heroStatementsPosition}
            highlightColor={page.heroHighlightColor}
            highlightPosition={page.heroHighlightPosition}
          />
        </div>
        {!!page.contentBlocks && (
          <div className={style.contentBlocks}>
            <ContentBlocks contentBlocks={page.contentBlocks} />
          </div>
        )}
      </div>
    </Layout>
  )
}

Page.propTypes = {
  data: PropTypes.shape({
    contentfulEdPages: PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      slug: PropTypes.string.isRequired,
      homepage: PropTypes.bool.isRequired,
      heroHeading: PropTypes.string.isRequired,
      heroIntroduction: PropTypes.shape({
        heroIntroduction: PropTypes.string.isRequired,
      }),
      heroHighlightColor: PropTypes.string.isRequired,
      heroHighlightPosition: PropTypes.string.isRequired,
      heroStatementsPosition: PropTypes.string.isRequired,
    }),
  }),
}

Page.defaultProps = {
  data: {},
}

export default observer(Page)

export const pageQuery = graphql`
  query($id: String!) {
    allResources: allContentfulEdResources {
      nodes {
        ...ContentfulEdResourcesFragment
      }
    }
    banner: contentfulEdBanners {
      id
      title
      content {
        content
      }
    }
    navigation: allContentfulEdPages(sort: { fields: order }) {
      nodes {
        id
        title
        slug
        homepage
        order
      }
    }
    page: contentfulEdPages(id: { eq: $id }) {
      id
      slug
			title
			metaTitle
      metaDescription {
        metaDescription
      }
      metaImage: heroImage {
        fixed(width: 1200, height: 630, cropFocus: FACES) {
          src
        }
      }
      homepage
      ...Hero
      contentBlocks {
        __typename
        ... on Node {
          ... on ContentfulEdContentPanels {
            id
            title
            actionLabel
            actionUrl
            color
            imagePosition
            backgroundColor
            image {
              id
              title
              desktop: fluid(
                maxWidth: 580
                maxHeight: 380
                resizingBehavior: FILL
                cropFocus: FACES
              ) {
                ...GatsbyContentfulFluid
              }
              mobile: fluid(
                maxWidth: 380
                maxHeight: 340
                resizingBehavior: FILL
                cropFocus: FACES
              ) {
                ...GatsbyContentfulFluid
              }
            }
            text {
              text
            }
          }
          ... on ContentfulEdHeadings {
            id
            mainHeading
            subHeading {
              subHeading
            }
            backgroundColor
          }
          ... on ContentfulEdIconGrids {
            id
            title
            icons {
              title
              fluid(maxWidth: 300) {
                ...GatsbyContentfulFluid
              }
            }
          }
          ... on ContentfulEdIconStrips {
            id
            title
            items {
              id
              title
              iconName
              text {
                text
              }
            }
            actionLabel
            actionUrl
          }
          ... on ContentfulEdPagePreviews {
            id
            title
            pages {
              ...ContentfulEdPagesFragment
            }
          }
          ... on ContentfulEdResourceGroups {
            id
            title
            backgroundColor
            showBottomBorder
            resources {
              ...ContentfulEdResourcesFragment
            }
          }
          ... on ContentfulEdSingleImages {
            id
            title
            highlightColor
            highlightPosition
            backgroundColor
            image {
              id
              title
              fluid(maxWidth: 680, cropFocus: FACES) {
                ...GatsbyContentfulFluid
              }
            }
          }
          ... on ContentfulEdSliders {
            id
            title
            backgroundColor
            cells {
              id
              heading
              image {
                id
                title
                fluid(
                  maxWidth: 700
                  maxHeight: 394
                  resizingBehavior: FILL
                  cropFocus: FACES
                ) {
                  ...GatsbyContentfulFluid
                }
              }
              videoUrl
              text {
                text
              }
              color
              actionLabel
              actionUrl
            }
          }
        }
      }
    }
  }

  fragment ContentfulEdPagesFragment on ContentfulEdPages {
    id
    title
    slug
    resources {
      ...ContentfulEdResourcesFragment
    }
    heroIntroduction {
      heroIntroduction
    }
  }

  fragment ContentfulEdResourcesFragment on ContentfulEdResources {
    id
    title
    url
    icon {
      id
      title
      fluid(maxWidth: 300) {
        ...GatsbyContentfulFluid
      }
    }
    keywords
    restricted
    videoResource
  }

  fragment Hero on ContentfulEdPages {
    heroHeading
    heroHighlightColor
    heroHighlightPosition
    heroIntroduction {
      heroIntroduction
    }
    heroStatements
    heroStatementsPosition
    heroImage {
      id
      title
      desktopPortrait: fluid(
        maxWidth: 450
        maxHeight: 550
        resizingBehavior: FILL
      ) {
        ...GatsbyContentfulFluid
      }
      desktopLandscape: fluid(
        maxWidth: 550
        maxHeight: 420
        resizingBehavior: FILL
      ) {
        ...GatsbyContentfulFluid
      }
      mobile: fluid(maxWidth: 380, maxHeight: 340, resizingBehavior: FILL) {
        ...GatsbyContentfulFluid
      }
      file {
        details {
          image {
            height
            width
          }
        }
      }
    }
  }
`
